import {  mergeWith, isArray } from 'lodash'
import { getCategoryLabelByValue, getPoiBySlug, getCategoryValueByLabel } from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import poiSettingsDefaults from "@data/poiSettingsDefaults";

const POI_QUERY_PARAMS_MAP = {
    trafficCategories: 'kategorie-verkehr',
    onlyFavourites: 'nurfavoriten',
    openAllDay: '24hoffen',
    openNextHour: 'naechstestundeoffen',
    oeamtcPartner: 'oeamtcpartner',
    accessible: 'barrierefrei',
    fuel: 'treibstoff',
    gasStationOperators: 'tankbetreiber',
    gasStationOperationMode: 'tankbetriebsart',
    gasStationServices: 'tankservices',
    chargingCapacity: 'ladeleistung',
    chargingPlugTypes: 'steckertypen',
    freeCharging: 'gratisladen',
    greenEnergy: 'oekostrom',
    oeamtcEPower: 'epower',
    carParkCategories: 'kategorie-parken',
    carParkPriceMode: 'parkpreis',
    carParkOperators: 'parkbetreiber',
    nearbyStationRadius: 'umkreis',
    sightCategories: 'kategorie-sehenswertes',
    oeamtcLocationCategories: 'kategorie-oeamtcstandorte',
    partnerCategories: 'kategorie-vorteilspartner',
    changingTable: 'wickelraum',
    playground: 'spielplatz',
    suitableForCampers: 'wohnwagengeeignet',
    openAllYear: 'ganzjaehrigoffen',
    campingCategories: 'kategorie-camping',
}

export const POI_OPTION_QUERY_PARAMS = Object.values(POI_QUERY_PARAMS_MAP)

const BASE_QUERY_OPTIONS = Object.fromEntries(Object.values(POI_QUERY_PARAMS_MAP).map(entry => [entry, '']))


export function getPoiBaseQueryOptions() {
    return JSON.parse(JSON.stringify(BASE_QUERY_OPTIONS))
}

export function encodePoiOptions(options, poiCategories) {
    let query = getPoiBaseQueryOptions()

    poiCategories.forEach(poiCategory => {
        const poiConfig = getPoiConfig(poiCategory)
        const poiOption = options[poiCategory]

        // general
        if(poiOption.other && poiOption.other.favouritesOnly) {
            query[POI_QUERY_PARAMS_MAP.onlyFavourites] += `${query[POI_QUERY_PARAMS_MAP.onlyFavourites] === '' ? '' : ','}${poiConfig.slug}`
        }
        if(poiOption.other && poiOption.other.openAllDay) {
            query[POI_QUERY_PARAMS_MAP.openAllDay] += `${query[POI_QUERY_PARAMS_MAP.openAllDay] === '' ? '' : ','}${poiConfig.slug}`
        }
        if(poiOption.other && poiOption.other.openNextHour) {
            query[POI_QUERY_PARAMS_MAP.openNextHour] += `${query[POI_QUERY_PARAMS_MAP.openNextHour] === '' ? '' : ','}${poiConfig.slug}`
        }
        if(poiOption.other && poiOption.other.oeamtcPartner) {
            query[POI_QUERY_PARAMS_MAP.oeamtcPartner] += `${query[POI_QUERY_PARAMS_MAP.oeamtcPartner] === '' ? '' : ','}${poiConfig.slug}`
        }
        if(poiOption.other && poiOption.other.accessible) {
            query[POI_QUERY_PARAMS_MAP.accessible] += `${query[POI_QUERY_PARAMS_MAP.accessible] === '' ? '' : ','}${poiConfig.slug}`
        }

        // traffic
        if(poiCategory === 'traffic' && poiOption.type) {
            query[POI_QUERY_PARAMS_MAP.trafficCategories] = poiOption.type.join(',')
        }

        // gasStations
        if(poiOption.fuels) {
            query[POI_QUERY_PARAMS_MAP.fuel] = poiOption.fuels[0]
        }
        if(poiOption.operator && poiCategory === 'gasStations') {
            query[POI_QUERY_PARAMS_MAP.gasStationOperators] = poiOption.operator.map(value => getCategoryLabelByValue(poiCategory, value)).join(',')
        }
        if(poiOption.operationMode && poiOption.operationMode !== '') {
            query[POI_QUERY_PARAMS_MAP.gasStationOperationMode] = poiOption.operationMode
        }
        if(poiOption.services && poiOption.services.length) {
            query[POI_QUERY_PARAMS_MAP.gasStationServices] = poiOption.services.join(',')
        }

        // chargingStations
        if(poiOption.capacity) {
            query[POI_QUERY_PARAMS_MAP.chargingCapacity] = poiOption.capacity.join('-')
        }
        if(poiOption.plugTypes) {
            query[POI_QUERY_PARAMS_MAP.chargingPlugTypes] = poiOption.plugTypes.join(',')
        }
        if(poiOption.other && poiOption.other.free) {
            query[POI_QUERY_PARAMS_MAP.freeCharging] = 'ja'
        }
        if(poiOption.other && poiOption.other.greenEnergy) {
            query[POI_QUERY_PARAMS_MAP.greenEnergy] = 'ja'
        }
        if(poiOption.other && poiOption.other.oeamtcEPower) {
            query[POI_QUERY_PARAMS_MAP.oeamtcEPower] = 'ja'
        }

        // carParks
        if(poiOption.categories && poiCategory === 'carParks') {
            query[POI_QUERY_PARAMS_MAP.carParkCategories] = poiOption.categories.join(',')
        }
        if(poiOption.priceMode) {
            query[POI_QUERY_PARAMS_MAP.carParkPriceMode] = poiOption.priceMode
        }
        if(poiOption.operator && poiCategory === 'carParks') {
            query[POI_QUERY_PARAMS_MAP.carParkOperators] = poiOption.operator.join(',')
        }

        // nearbyStations
        if(poiOption.radius) {
            query[POI_QUERY_PARAMS_MAP.nearbyStationRadius] = poiOption.radius
        }

        // sights
        if(poiOption.categories && poiCategory === 'sights') {
            query[POI_QUERY_PARAMS_MAP.sightCategories] = poiOption.categories.join(',')
        }

        // oeamtcLocations
        if(poiOption.categories && poiCategory === 'oeamtcLocations') {
            query[POI_QUERY_PARAMS_MAP.oeamtcLocationCategories] = poiOption.categories.join(',')
        }

        // partners
        if(poiOption.categories && poiCategory === 'partners') {
            query[POI_QUERY_PARAMS_MAP.partnerCategories] = poiOption.categories.join(',')
        }

        // roadHouses
        if(poiOption.facility) {
            if(poiOption.facility.changingTable) query[POI_QUERY_PARAMS_MAP.changingTable] = 'ja'
            if(poiOption.facility.playground) query[POI_QUERY_PARAMS_MAP.playground] = 'ja'
        }

        // tollRoads, passRoads
        if(poiOption.roadConnection) {
            if(poiOption.roadConnection.suitableForCampers) query[POI_QUERY_PARAMS_MAP.suitableForCampers] += `${query[POI_QUERY_PARAMS_MAP.suitableForCampers] === '' ? '' : ','}${poiConfig.slug}`
            if(poiOption.roadConnection.openAllYear) query[POI_QUERY_PARAMS_MAP.openAllYear] += `${query[POI_QUERY_PARAMS_MAP.openAllYear] === '' ? '' : ','}${poiConfig.slug}`
        }

        // camping
        if(poiOption.categories && poiCategory === 'camping') {
            query[POI_QUERY_PARAMS_MAP.campingCategories] = poiOption.categories.join(',')
        }
    })

    return query
}

export function decodePoiOptions(query) {
    const defaultPoiOptions = JSON.parse(JSON.stringify(poiSettingsDefaults))
    const queryPoiOptions = {}

    // general
    if(query[POI_QUERY_PARAMS_MAP.onlyFavourites]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.onlyFavourites])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].other) queryPoiOptions[poiCategory].other = {}
                queryPoiOptions[poiCategory].other.favouritesOnly = true
            })
        }
    }
    if(query[POI_QUERY_PARAMS_MAP.openAllDay]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.openAllDay])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].other) queryPoiOptions[poiCategory].other = {}
                queryPoiOptions[poiCategory].other.openAllDay = true
            })
        }
    }
    if(query[POI_QUERY_PARAMS_MAP.openNextHour]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.openNextHour])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].other) queryPoiOptions[poiCategory].other = {}
                queryPoiOptions[poiCategory].other.openNextHour = true
            })
        }
    }
    if(query[POI_QUERY_PARAMS_MAP.oeamtcPartner]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.oeamtcPartner])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].other) queryPoiOptions[poiCategory].other = {}
                queryPoiOptions[poiCategory].other.oeamtcPartner = true
            })
        }
    }
    if(query[POI_QUERY_PARAMS_MAP.accessible]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.accessible])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].other) queryPoiOptions[poiCategory].other = {}
                queryPoiOptions[poiCategory].other.accessible = true
            })
        }
    }

    // traffic
    if(query[POI_QUERY_PARAMS_MAP.trafficCategories]) {
        if(!queryPoiOptions.traffic) queryPoiOptions.traffic = {} 
        queryPoiOptions.traffic.type = query[POI_QUERY_PARAMS_MAP.trafficCategories].split(',')
    }

    // gasStations
    if(query[POI_QUERY_PARAMS_MAP.fuel]) {
        if(!queryPoiOptions.gasStations) queryPoiOptions.gasStations = {} 
        queryPoiOptions.gasStations.fuels = [query[POI_QUERY_PARAMS_MAP.fuel]]
    }
    if(query[POI_QUERY_PARAMS_MAP.gasStationOperators]) {
        if(!queryPoiOptions.gasStations) queryPoiOptions.gasStations = {} 
        queryPoiOptions.gasStations.operator = query[POI_QUERY_PARAMS_MAP.gasStationOperators].split(',').map(label => getCategoryValueByLabel('gasStations', label))
    }
    if(query[POI_QUERY_PARAMS_MAP.gasStationOperationMode]) {
        if(!queryPoiOptions.gasStations) queryPoiOptions.gasStations = {} 
        queryPoiOptions.gasStations.operationMode = query[POI_QUERY_PARAMS_MAP.gasStationOperationMode]
    }
    if(query[POI_QUERY_PARAMS_MAP.gasStationServices]) {
        if(!queryPoiOptions.gasStations) queryPoiOptions.gasStations = {} 
        queryPoiOptions.gasStations.services = query[POI_QUERY_PARAMS_MAP.gasStationServices].split(',')
    }

    // chargingStations
    if(query[POI_QUERY_PARAMS_MAP.chargingCapacity]) {
        if(!queryPoiOptions.chargingStations) queryPoiOptions.chargingStations = {} 
        queryPoiOptions.chargingStations.capacity = query[POI_QUERY_PARAMS_MAP.chargingCapacity].split('-').map(value => parseInt(value))
    }
    if(query[POI_QUERY_PARAMS_MAP.chargingPlugTypes]) {
        if(!queryPoiOptions.chargingStations) queryPoiOptions.chargingStations = {} 
        queryPoiOptions.chargingStations.plugTypes = query[POI_QUERY_PARAMS_MAP.chargingPlugTypes].split(',')
    }
    if(query[POI_QUERY_PARAMS_MAP.freeCharging] && query[POI_QUERY_PARAMS_MAP.freeCharging] === 'ja') {
        if(!queryPoiOptions.chargingStations) queryPoiOptions.chargingStations = {} 
        if(!queryPoiOptions.chargingStations.other) queryPoiOptions.chargingStations.other = {}
        queryPoiOptions.chargingStations.other.free = true
    }
    if(query[POI_QUERY_PARAMS_MAP.greenEnergy] && query[POI_QUERY_PARAMS_MAP.greenEnergy] === 'ja') {
        if(!queryPoiOptions.chargingStations) queryPoiOptions.chargingStations = {} 
        if(!queryPoiOptions.chargingStations.other) queryPoiOptions.chargingStations.other = {}
        queryPoiOptions.chargingStations.other.greenEnergy = true
    }
    if(query[POI_QUERY_PARAMS_MAP.oeamtcEPower] && query[POI_QUERY_PARAMS_MAP.oeamtcEPower] === 'ja') {
        if(!queryPoiOptions.chargingStations) queryPoiOptions.chargingStations = {} 
        if(!queryPoiOptions.chargingStations.other) queryPoiOptions.chargingStations.other = {}
        queryPoiOptions.chargingStations.other.oeamtcEPower = true
    }

    // carParks
    if(query[POI_QUERY_PARAMS_MAP.carParkCategories]) {
        if(!queryPoiOptions.carParks) queryPoiOptions.carParks = {} 
        queryPoiOptions.carParks.categories = query[POI_QUERY_PARAMS_MAP.carParkCategories].split(',')
    }
    if(query[POI_QUERY_PARAMS_MAP.carParkPriceMode]) {
        if(!queryPoiOptions.carParks) queryPoiOptions.carParks = {} 
        queryPoiOptions.carParks.priceMode = query[POI_QUERY_PARAMS_MAP.carParkPriceMode]
    }
    if(query[POI_QUERY_PARAMS_MAP.carParkOperators]) {
        if(!queryPoiOptions.carParks) queryPoiOptions.carParks = {} 
        queryPoiOptions.carParks.operator = query[POI_QUERY_PARAMS_MAP.carParkOperators].split(',')
    }

    // nearbyStations
    if(query[POI_QUERY_PARAMS_MAP.nearbyStationRadius]) {
        if(!queryPoiOptions.nearbyStations) queryPoiOptions.nearbyStations = {} 
        queryPoiOptions.nearbyStations.radius = parseInt(query[POI_QUERY_PARAMS_MAP.nearbyStationRadius])
    }

    // sights
    if(query[POI_QUERY_PARAMS_MAP.sightCategories]) {
        if(!queryPoiOptions.sights) queryPoiOptions.sights = {} 
        queryPoiOptions.sights.categories = query[POI_QUERY_PARAMS_MAP.sightCategories].split(',')
    }

    // oeamtcLocations
    if(query[POI_QUERY_PARAMS_MAP.oeamtcLocationCategories]) {
        if(!queryPoiOptions.oeamtcLocations) queryPoiOptions.oeamtcLocations = {} 
        queryPoiOptions.oeamtcLocations.categories = query[POI_QUERY_PARAMS_MAP.oeamtcLocationCategories].split(',')
    }

    // partners
    if(query[POI_QUERY_PARAMS_MAP.partnerCategories]) {
        if(!queryPoiOptions.partners) queryPoiOptions.partners = {} 
        queryPoiOptions.partners.categories = query[POI_QUERY_PARAMS_MAP.partnerCategories].split(',')
    }

    // roadHouses
    if(query[POI_QUERY_PARAMS_MAP.changingTable] && query[POI_QUERY_PARAMS_MAP.changingTable] === 'ja') {
        if(!queryPoiOptions.roadHouses) queryPoiOptions.roadHouses = {} 
        if(!queryPoiOptions.roadHouses.facility) queryPoiOptions.roadHouses.facility = {}
        queryPoiOptions.roadHouses.facility.changingTable = true
    }
    if(query[POI_QUERY_PARAMS_MAP.playground] && query[POI_QUERY_PARAMS_MAP.playground] === 'ja') {
        if(!queryPoiOptions.roadHouses) queryPoiOptions.roadHouses = {} 
        if(!queryPoiOptions.roadHouses.facility) queryPoiOptions.roadHouses.facility = {}
        queryPoiOptions.roadHouses.facility.playground = true
    }

    // tollRoads, passRoads
    if(query[POI_QUERY_PARAMS_MAP.suitableForCampers]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.suitableForCampers])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].roadConnection) queryPoiOptions[poiCategory].roadConnection = {}
                queryPoiOptions[poiCategory].roadConnection.suitableForCampers = true
            })
        }
    }
    if(query[POI_QUERY_PARAMS_MAP.openAllYear]) {
        const poiCategories = decodeSlugStringQuery(query[POI_QUERY_PARAMS_MAP.openAllYear])
        if(poiCategories) {
            poiCategories.forEach(poiCategory => {
                if(!queryPoiOptions[poiCategory]) queryPoiOptions[poiCategory] = {} 
                if(!queryPoiOptions[poiCategory].roadConnection) queryPoiOptions[poiCategory].roadConnection = {}
                queryPoiOptions[poiCategory].roadConnection.openAllYear = true
            })
        }
    }

    // camping
    if(query[POI_QUERY_PARAMS_MAP.campingCategories]) {
        if(!queryPoiOptions.camping) queryPoiOptions.camping = {} 
        queryPoiOptions.camping.categories = query[POI_QUERY_PARAMS_MAP.campingCategories].split(',')
    }


    if(Object.keys(queryPoiOptions).length > 0) {
        const mergedPoiOptions = {}
        for(const key in defaultPoiOptions) {
           mergedPoiOptions[key] = mergeWith(defaultPoiOptions[key], queryPoiOptions[key] || {}, mergeCustomizer)
        }
        return mergedPoiOptions
    }
    return false
}

function mergeCustomizer(objValue, srcValue) {
    if(isArray(objValue) && isArray(srcValue)) {
        return srcValue
    }
}

function decodeSlugStringQuery(query) {
    const defaultPoiOptions = JSON.parse(JSON.stringify(poiSettingsDefaults))
    const poiCategories = []
    const poiSlugs = query.split(',')
    poiSlugs.forEach(slug => {
        const poiConfig = getPoiBySlug(slug)
        if(poiConfig) {
            const poiCategory = poiConfig.value
            if(defaultPoiOptions[poiCategory].other) {
                poiCategories.push(poiCategory)
            }
        }
    })
    return poiCategories.length ? poiCategories : false
}